import * as ModalPrimitive from '@radix-ui/react-dialog';
import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../../../common-utils/components/renders/icon/Icon';
import ICONS from '../../../common-utils/enums/icons';
import { cn } from '../../../common-utils/libraries/tailwind/utils';

const Modal = ModalPrimitive.Root;

const ModalTrigger = ModalPrimitive.Trigger;

const ModalClose = ModalPrimitive.Close;

const ModalOverlay = React.forwardRef((({ className, children, ...props }, ref) => (
  <ModalPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-1001 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out overflow-auto',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'modal-overlay',
      className
    )}
    {...props}
  >
    {children}
  </ModalPrimitive.Overlay>
)));

ModalOverlay.displayName = ModalPrimitive.Overlay.displayName;

ModalOverlay.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

const ModalContent = React.forwardRef((({ className, children, ...props }, ref) => (
  <ModalOverlay >
    <ModalPrimitive.Content
      onOpenAutoFocus={(e) => e.preventDefault()}
      ref={ref}
      className={cn(
        'relative height-auto sm:mx-auto mx-6 my-10 z-101 grid w-auto gap-2 border bg-background shadow-lg',
        'duration-100 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        'data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]',
        'data-[state=open]:scale-100 data-[state=closed]:scale-95 sm:rounded-lg',
        className
      )}
      onPointerDownOutside={(e) => e.preventDefault()}
      {...props}
    >
      {children}
      <ModalPrimitive.Close
        data-cy='modal-close-icon'
        className={cn('absolute flex left-4 top-4 rounded-sm opacity-80 ring-offset-background transition-opacity hover:opacity-100 hover:bg-gray-200',
          'focus:outline-none focus:ring-blue-500 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground')} >
        <Icon icon={ICONS.CLOSE} className={cn('font-8')} />
      </ModalPrimitive.Close>
    </ModalPrimitive.Content>
  </ModalOverlay >
)));

ModalContent.displayName = ModalPrimitive.Content.displayName;

ModalContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

const ModalHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      'flex flex-col w-[100%] border-b px-3 py-4 space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);

ModalHeader.displayName = 'DialogHeader';

ModalHeader.propTypes = {
  className: PropTypes.string
};

const ModalTitle = React.forwardRef((({ className, ...props }, ref) => (
  <ModalPrimitive.Title
    ref={ref}
    className={cn(
      'text-xl text-center font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
)));

ModalTitle.displayName = ModalPrimitive.Title.displayName;

ModalTitle.propTypes = {
  className: PropTypes.string
};


const ModalBody = React.forwardRef((({ children, className, onScroll, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-md pt-1 pl-4 pb-1 pr-4',
      className)}
    onScroll={onScroll}
    {...props}
  >
    {children}
  </div>
)));

ModalBody.displayName = ModalPrimitive.Description.displayName;

ModalBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onScroll: PropTypes.func
};
const ModalFooter = ({
  children,
  className,
  ...props
}) => (
  <div
    className={cn(
      'flex flex-col p-3 border-t sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

ModalFooter.displayName = 'DialogFooter';

ModalFooter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

export {
  Modal,
  ModalTrigger,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalClose,
  ModalFooter
};